import clsx from "clsx";
import { TriangleAlert, Wrench, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { Announcement } from "../api/types";
import { Disclosure } from "../components/panel";
import { useLastDefined } from "../components/presence";
import { IconButton } from "../components/ui";
import { t } from "../i18n/i18n";
import {
  bannerCountdown,
  bannerShown,
  bannerTitle,
  dismissAnnouncement,
  messageFor,
  phaseAt,
  type Phase,
  slotText,
  startAnnouncementWatch,
  useAnnouncement,
} from "./announcement";

/** Bandeau de l'annonce d'une maintenance (registre F40) : sous le bandeau « PRÉPRODUCTION », au-dessus de tous les
 *  écrans (activation de la licence, connexion, lecture seule, accueil, espace de travail). Discret pour une
 *  information (fond bleu clair), plus marqué pour une interruption de service (fond ambre d'avertissement, filet et
 *  icône ambre ; jamais le rouge de l'ennemi). Il pousse l'écran vers le bas en s'ouvrant en hauteur et en fondu
 *  (`Disclosure`), et se referme de même. Titre et compte à rebours en clair (« début dans 2 h »), texte de
 *  l'annonce dans la langue de l'interface, créneau à l'heure de Paris. La croix le ferme pour la session : il
 *  revient 15 minutes avant le début, puis au début ; il disparaît de lui-même à la fin. `preview` : aperçu de la
 *  fenêtre de la préproduction (sans croix, sans animation d'entrée). */
export function AnnouncementBanner({
  announcement,
  phase,
  now,
  onDismiss,
  preview = false,
}: {
  announcement: Announcement;
  phase: Phase;
  now: number;
  onDismiss?: () => void;
  preview?: boolean;
}) {
  const outage = announcement.level === "outage";
  const Icon = outage ? TriangleAlert : Wrench;
  return (
    <div
      role={preview ? undefined : "status"}
      aria-live={preview ? undefined : "polite"}
      data-announcement-banner={announcement.level}
      data-phase={phase}
      className={clsx(
        "flex items-start gap-2.5 py-2 pr-2 pl-3",
        outage ? "border-warn/40 bg-warn-soft" : "border-accent/20 bg-accent-soft",
        preview ? "rounded-lg border" : "border-b",
      )}
    >
      <Icon size={15} aria-hidden className={clsx("mt-0.5 shrink-0", outage ? "text-warn" : "text-accent")} />
      <p className="min-w-0 flex-1 py-px text-meta leading-snug break-words text-ink-soft">
        <span className={clsx("font-semibold", outage ? "text-warn" : "text-ink")}>{bannerTitle(announcement.level, phase)}</span>
        <span
          className={clsx(
            "mx-1.5 inline-block rounded-full px-1.5 font-medium tabular-nums",
            outage ? "bg-warn/10 text-warn" : "bg-accent/10 text-accent",
          )}
        >
          {bannerCountdown(announcement, phase, now)}
        </span>
        <span className="text-ink">{messageFor(announcement)}</span>
        <span className="ml-1.5 text-ink-muted">{t("({slot}, heure de Paris)", { slot: slotText(announcement) })}</span>
      </p>
      {onDismiss && (
        <IconButton
          size="xs"
          data-announcement-dismiss
          className="-my-0.5 shrink-0"
          onClick={onDismiss}
          title={phase === "running" ? t("Masquer") : t("Masquer jusqu'à l'approche de la maintenance")}
          aria-label={phase === "running" ? t("Masquer") : t("Masquer jusqu'à l'approche de la maintenance")}
        >
          <X size={14} />
        </IconButton>
      )}
    </div>
  );
}

/** Rythme de l'horloge du bandeau (compte à rebours, retour à l'approche, disparition à la fin). */
export const TICK_MS = 15_000;

/** Bandeau lu dans l'état de l'onglet ; lance la relecture de l'annonce (toutes les 5 min, au retour sur la page).
 *  Rien ne s'affiche sans annonce, et rien n'échoue si elle est illisible. */
export function AnnouncementHost() {
  const announcement = useAnnouncement((s) => s.announcement);
  const dismissed = useAnnouncement((s) => s.dismissed);
  const offset = useAnnouncement((s) => s.offset);
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => startAnnouncementWatch(), []);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);
  // Heure du serveur : horloge du poste corrigée de l'écart relevé à la dernière lecture.
  const now = clock + offset;
  const shown = bannerShown(announcement, now, dismissed);
  const phase = announcement ? phaseAt(announcement, now) : null;
  // Contenu gardé le temps de la fermeture (en hauteur et en fondu).
  const last = useLastDefined(shown && announcement && phase ? { announcement, phase } : null);
  // Première apparition : posé fermé, puis ouvert à l'image suivante (ouverture animée, jamais d'un coup). Sans
  // annonce reçue, rien n'est rendu.
  const present = Boolean(last);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!present) return;
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [present]);
  if (!last) return null;
  return (
    <Disclosure open={shown && entered} className="relative z-[84] shrink-0">
      <AnnouncementBanner
        announcement={last.announcement}
        phase={last.phase}
        now={now}
        onDismiss={() => dismissAnnouncement(now)}
      />
    </Disclosure>
  );
}
